import React, { useState, useEffect } from 'react';
import SingerHistoryService from '../services/SingerHistoryService';

interface SongHistory {
  id?: string;
  songId: string;
  title: string;
  artist?: string;
  videoId?: string;
  source: 'youtube' | 'local';
  singerName: string;
  timestamp: Date;
  timesPerformed?: number;
}

interface SingerHistoryPanelProps {
  userId?: string;
  onSelectSong?: (song: { id: string; title: string; artist?: string; videoId?: string; source: string }) => void;
  className?: string;
}

const SingerHistoryPanel: React.FC<SingerHistoryPanelProps> = ({ userId, onSelectSong, className = '' }) => {
  const [history, setHistory] = useState<SongHistory[]>([]);
  const [favorites, setFavorites] = useState<(SongHistory & { timesPerformed: number })[]>([]);
  const [view, setView] = useState<'recent' | 'favorites'>('recent');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [userId]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const [recentSongs, favoriteSongs] = await Promise.all([
        SingerHistoryService.getHistory(userId, 10),
        SingerHistoryService.getFavorites(userId, 5)
      ]);
      setHistory(recentSongs);
      setFavorites(favoriteSongs);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSong = (song: SongHistory) => {
    if (onSelectSong) {
      onSelectSong({
        id: song.songId,
        title: song.title,
        artist: song.artist,
        videoId: song.videoId,
        source: song.source
      });
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-gray-100 border border-gray-300 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-qr-primary"></div>
          <span className="ml-3 text-gray-600">Loading your songs...</span>
        </div>
      </div>
    );
  }

  if (history.length === 0 && favorites.length === 0) {
    return null; // Don't show panel if no history
  }

  const displaySongs = view === 'recent' ? history : favorites;

  return (
    <div className={`bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-purple-900 flex items-center">
          <span className="text-2xl mr-2">🎤</span>
          My Songs
        </h3>
        <div className="flex bg-white rounded-lg border border-purple-300">
          <button
            onClick={() => setView('recent')}
            className={`px-3 py-1 text-sm rounded-l-lg transition-colors ${
              view === 'recent'
                ? 'bg-purple-600 text-white'
                : 'text-purple-600 hover:bg-purple-100'
            }`}
          >
            Recent
          </button>
          <button
            onClick={() => setView('favorites')}
            className={`px-3 py-1 text-sm rounded-r-lg transition-colors ${
              view === 'favorites'
                ? 'bg-purple-600 text-white'
                : 'text-purple-600 hover:bg-purple-100'
            }`}
          >
            Favorites
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {displaySongs.length === 0 ? (
          <p className="text-purple-700 text-sm text-center py-4">
            {view === 'recent' ? 'No recent songs' : 'No favorite songs yet'}
          </p>
        ) : (
          displaySongs.map((song, index) => (
            <div
              key={`${song.songId}-${index}`}
              className="bg-white border border-purple-200 rounded-lg p-3 hover:border-purple-400 hover:shadow-md transition-all cursor-pointer"
              onClick={() => handleSelectSong(song)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-purple-900 text-sm">{song.title}</p>
                  {song.artist && (
                    <p className="text-purple-600 text-xs">{song.artist}</p>
                  )}
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-purple-500">
                      {song.source === 'youtube' ? '📺 YouTube' : '💿 Local'}
                    </span>
                    {song.timesPerformed && song.timesPerformed > 1 && (
                      <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">
                        Sung {song.timesPerformed}x
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectSong(song);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Add Again
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {displaySongs.length > 0 && (
        <div className="mt-3 text-center">
          <p className="text-xs text-purple-600">
            👆 Click any song to add it to the queue again!
          </p>
        </div>
      )}
    </div>
  );
};

export default SingerHistoryPanel;
